/**
 * ROARDRIVE speaks the driver's language.
 *
 * Every word a driver reads outside the dash itself lives here. The dash is deliberately NOT translated:
 * a race display says LAP, GEAR and RACE in English on the real car too, and Marc's Cosworth is a copy of
 * a real one (see the frozen HUD rules in the project notes).
 *
 * The language follows the phone (or the car's browser) by itself and can be changed in the menu, under
 * the account. The choice is stored with the other settings, so it travels with the account.
 */
import { useEffect, useState } from 'preact/hooks';
import { loadSettings, saveSettings } from '../utils/settings';
import { STRINGS, LANGS, type Lang, type Key } from './strings';

export { LANGS };
export type { Lang, Key };

const CODES = LANGS.map((l) => l.code);

/** the phone's own language, mapped onto what we speak; anything else lands on English */
export function detectLang(): Lang {
  const wanted = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || 'en']);
  for (const raw of wanted) {
    const tag = String(raw).toLowerCase();
    const base = tag.split('-')[0] as Lang;
    if (CODES.includes(base)) return base;
  }
  return 'en';
}

let stored: Lang | null = null;
try {
  const s = loadSettings().lang;
  stored = s && CODES.includes(s as Lang) ? (s as Lang) : null;
} catch { /* private mode */ }
let current: Lang = stored ?? detectLang();

const listeners = new Set<() => void>();

export function getLang(): Lang { return current; }
/** null = follow the phone */
export function getLangSetting(): Lang | null { return stored; }

export function setLang(lang: Lang | null): void {
  stored = lang;
  current = lang ?? detectLang();
  saveSettings({ lang });
  try { document.documentElement.lang = current; } catch { /* no document */ }
  listeners.forEach((f) => f());
}

/** t('key') or t('key', { name: 'value' }) for the {name} placeholders */
export function t(key: Key, vars?: Record<string, string | number>): string {
  const dict = STRINGS[current] ?? STRINGS.en;
  let s = dict[key] ?? STRINGS.en[key] ?? String(key);
  if (vars) for (const k of Object.keys(vars)) s = s.split(`{${k}}`).join(String(vars[k]));
  return s;
}

/** components use this so a language change repaints them without a reload */
export function useT(): typeof t {
  const [, bump] = useState(0);
  useEffect(() => {
    const f = () => bump((n) => n + 1);
    listeners.add(f);
    return () => { listeners.delete(f); };
  }, []);
  return t;
}

try { document.documentElement.lang = current; } catch { /* no document */ }
